import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import queryString from 'query-string'
import { first, isEmpty } from 'lodash'
import moment from 'moment'
import classNames from 'classnames'
import {
  IconCalendar,
  IconPhone,
  IconFileTypePdf,
  IconArrowRight,
  IconMail,
  IconMessage,
  IconDeviceMobile,
} from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { PageHeader } from '@/components/headers/PageHeader'
import { Card } from '@/components/cards/Card'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { ExternalLink } from '@/components/ExternalLink'
import { formatTime } from '@/utils/helpers'
import NoData from '@/components/NoData'
import { setData, setFilter } from '@/reducers/scorecards/scorecards.redux'
import {
  fetchAggregateScoresByConfigId,
  fetchAggregateScores,
  fetchNotableCalls,
  fetchScoresByScorecardId,
  removeSelectedScorecard,
  selectScorecard,
} from '@/reducers/scorecards/scorecards.actions'
import { CHANNEL_TYPE, CHANNEL_NAME_PLURAL_DISPLAY, CHANNEL_NAME_DISPLAY } from '@/utils/constants'
import { useShouldHideCustomerId } from '@/utils/hooks/useShouldHideCustomerId'
import { calculatePercentage } from '../CallScoreCarousel/helpers'
import { MeasureScoresTable } from '../MeasureScoresTable/MeasureScoresTable'

import { ScoreAccordion } from '../ScoreAccordion/ScoreAccordion'
import { getScoreThreshold } from '../helpers'
import { CallScoreCarousel } from '../CallScoreCarousel/CallScoreCarousel'
import { ViewScoresFilters } from '../ViewScoresFilters/ViewScoresFilters'

import './viewScoresPage.css'

const ABOVE_THRESHOLD = 'aboveThreshold'
const BELOW_THRESHOLD = 'belowThreshold'

const getChannelIcon = (channelType) => {
  switch (channelType) {
    case CHANNEL_TYPE.EMAIL:
      return IconMail
    case CHANNEL_TYPE.SMS:
      return IconMessage
    case CHANNEL_TYPE.CHAT:
      return IconDeviceMobile
    case CHANNEL_TYPE.CALL:
    default:
      return IconPhone
  }
}

const getColumns = (channelType, shouldHideCustomerId = false) => {
  const channelName = CHANNEL_NAME_DISPLAY[channelType]

  const columns = [
    { accessor: 'start_time', label: 'Date and Time' },
    { accessor: 'name', label: 'Name' },
    { accessor: 'duration', label: 'Duration' },
    { accessor: 'aggregate_score', label: `${channelName} Score` },
    { accessor: 'voip_call_id', label: 'VoIP ID' },
    { accessor: 'voip_customer_id', label: 'VoIP Customer Id', redactedOrgHidden: true },
    { accessor: 'exploreCall', label: `Explore ${channelName}` },
  ]

  return columns.filter((column) => {
    if (channelType === CHANNEL_TYPE.EMAIL && column.accessor === 'duration') {
      return false
    }
    if (shouldHideCustomerId && column.redactedOrgHidden) {
      return false
    }
    return true
  })
}

const ViewScoresMetadata = ({
  dateRange,
  numScores,
  displayScorecards,
  showTableText,
  channelType,
}) => {
  const ChannelIcon = getChannelIcon(channelType)
  const channelLabel = CHANNEL_NAME_PLURAL_DISPLAY[channelType].toLowerCase()

  return (
    <div
      className="flex-align-center flex-space-between normal-weight print-hidden"
      data-testid="view-scores-metadata"
    >
      <div className="flex-align-center gap">
        <span className="flex-align-center small-gap">
          <IconCalendar className="icon-svg status-informative" />
          {dateRange}
        </span>
        <span className="flex-align-center small-gap">
          <ChannelIcon className="icon-svg status-success" />
          {numScores} {channelLabel}
        </span>
      </div>
      <Button data-testid="view-all-calls" onClick={displayScorecards}>
        {showTableText}
        <IconArrowRight />
      </Button>
    </div>
  )
}

export const NoScoresMessage = () => {
  return (
    <div className="empty-table" data-testid="no-scores">
      <NoData />
    </div>
  )
}

export const ViewScoresLoader = () => {
  return (
    <div className="empty-table" data-testid="view-scores-loader">
      <Loader centered data-testid="loader" />
    </div>
  )
}

export const PrintScoresComponent = ({
  orgName,
  filters,
  aggregateScores,
  numScores,
  targetScorecardConfig,
}) => {
  const startDate = moment(filters?.startDate).local().format('MM/DD/YYYY')
  const endDate = moment(filters?.endDate).local().format('MM/DD/YYYY')
  const allAgents = filters.agents.map((agent) => agent.label)
  const allTags = filters.tags.map((tag) => tag.label)
  const successClass = getScoreThreshold(aggregateScores.percentage_score, targetScorecardConfig)
  return (
    <div className="view-scores-page_print_details">
      <div>
        <h1 style={{ textAlign: 'center' }}>{orgName} Scores</h1>
      </div>
      <div>
        <strong>Date Range:</strong> {startDate} - {endDate}
      </div>
      <div>
        <strong>Scorecard Config:</strong> {filters?.scorecards?.label}
      </div>
      <div>
        <strong>Agents:</strong> {allAgents.join(', ')}
      </div>
      <div>
        <strong>Tags:</strong> {allTags.join(', ')}
      </div>
      <div>
        <div style={{ float: 'left' }}>Total Calls: {numScores}</div>
        <div className={`view-scores-page__overall-score ${successClass}`}>
          Score Total: {aggregateScores.percentage_score}%
        </div>
      </div>
    </div>
  )
}

export const ViewScoresPageContent = ({ printScores, toggleTable, showTable }) => {
  const dispatch = useDispatch()
  const { data, loading } = useSelector((state) => state.scorecards)
  const [selectedScorecards, setSelectedScorecards] = useState([])
  const [activeThreshold, setActiveThreshold] = useState(null)
  const { aggregateScores } = data
  const { targetScorecardConfig } = data
  const { section_scores: sectionScores } = aggregateScores
  const closeCarousel = () => {
    dispatch(removeSelectedScorecard())
    dispatch(setData('displayNotableCalls', false))
  }

  const displayScorecards = () => {
    if (!showTable) {
      toggleTable(true)
      dispatch(fetchScoresByScorecardId())
    } else {
      toggleTable(false)
    }
  }

  const getNotableCalls = (section) => {
    dispatch(fetchNotableCalls(section))
  }

  const displayNotableCalls = (section, threshold, thresholdType = '') => {
    setActiveThreshold(thresholdType)
    dispatch(setData('displayNotableCalls', true))
    setSelectedScorecards([section])
    dispatch(selectScorecard(first(threshold[section.section_id])))
    const notableScoreIds = threshold[section.section_id]?.map((score) => score.id)
    dispatch(setData('scores', notableScoreIds))
  }

  const isThresholdActive = (sectionData, thresholdType) => {
    return (
      selectedScorecards.length > 0 &&
      sectionData.section_id === first(selectedScorecards)?.section_id &&
      data.displayNotableCalls &&
      activeThreshold === thresholdType
    )
  }

  const renderNotableCallsHeader = (sectionData) => {
    if (loading.notableCalls) {
      return <Loader data-testid="loader" size="small" />
    }
    if (
      data.aboveThreshold[sectionData.section_id]?.length > 0 ||
      data.belowThreshold[sectionData.section_id]?.length > 0
    ) {
      return (
        <div className="flex-align-center flex-space-between gap">
          <strong data-testid="view-notable-calls-length">Notable Calls</strong>
          <div className="flex-align-center small-gap">
            <Button
              small
              brand={isThresholdActive(sectionData, BELOW_THRESHOLD)}
              data-testid="view-notable-calls"
              onClick={() => displayNotableCalls(sectionData, data.belowThreshold, BELOW_THRESHOLD)}
              disabled={data.belowThreshold[sectionData.section_id]?.length === 0}
            >
              Below Threshold ({`${data.belowThreshold[sectionData.section_id]?.length}`})
            </Button>
            <Button
              small
              brand={isThresholdActive(sectionData, ABOVE_THRESHOLD)}
              data-testid="view-notable-calls_above_threshold"
              onClick={() => displayNotableCalls(sectionData, data.aboveThreshold, ABOVE_THRESHOLD)}
              disabled={data.aboveThreshold[sectionData.section_id]?.length === 0}
            >
              Above Threshold ({`${data.aboveThreshold[sectionData.section_id]?.length}`})
            </Button>
          </div>
        </div>
      )
    }
    return <div data-testid="no-notable-calls" />
  }

  const sectionAccordions =
    sectionScores &&
    Object.entries(sectionScores).map(([sectionName, sectionData]) => {
      const { measure_scores: measureScores } = sectionData
      const measureRows = Object.entries(measureScores).map(([name, measureData]) => ({
        name,
        aggregate_score: measureData.aggregate_score,
        possible_score: measureData.possible_score,
        percentage_score: measureData.percentage_score,
        id: measureData.measure_id,
      }))

      return (
        <ScoreAccordion
          title={sectionName}
          score={sectionData.percentage_score}
          key={sectionName}
          withColor
          sectionData={sectionData}
          getNotableCalls={getNotableCalls}
          aggregateSection
          showAllAccordions={printScores}
        >
          <MeasureScoresTable measureScores={measureRows} />
          {renderNotableCallsHeader(sectionData)}
        </ScoreAccordion>
      )
    })
  const successClass = getScoreThreshold(aggregateScores.percentage_score, targetScorecardConfig)
  const startDate = moment(data?.aggregateFilters?.start_date).local().format('MM/DD/YYYY')
  const endDate = moment(data?.aggregateFilters?.end_date).local().format('MM/DD/YYYY')
  const dateRange = `${startDate} - ${endDate}  `
  const scoresLength = data.allScores?.length || 0
  const channelType = data.appliedCallType || CHANNEL_TYPE.CALL
  const channelLabel = CHANNEL_NAME_PLURAL_DISPLAY[channelType]
  const showTableText =
    showTable && data.scorecardConfigs?.length > 0
      ? `Hide All ${channelLabel}`
      : `Show All ${channelLabel}`

  if (isEmpty(aggregateScores) || scoresLength === 0) {
    return <NoScoresMessage />
  }
  return (
    <div className="flex-column gap">
      <div className="half-width">
        <ViewScoresMetadata
          dateRange={dateRange}
          numScores={scoresLength}
          displayScorecards={displayScorecards}
          showTableText={showTableText}
          channelType={channelType}
        />
      </div>

      {!isEmpty(aggregateScores) ? (
        <div
          className="view-scores-page__metrics-section"
          data-testid="view-scores-metrics-section"
        >
          <div className="view-scores-page__overall-metrics">
            {sectionAccordions}
            <span className="view-scores-page__overall-score-section">
              <h3 className="mt">
                Aggregate Score:
                <span
                  className={classNames({
                    'status-success': successClass === 'exceeds_threshold',
                    'status-warning': successClass === 'meets_threshold',
                    'status-caution': successClass === 'improvement_threshold',
                    'status-critical': successClass === 'failure',
                  })}
                  style={{ marginLeft: '0.5rem' }}
                  data-testid="overall-score"
                >
                  {`${aggregateScores.percentage_score}%`}
                </span>
              </h3>
            </span>
          </div>
          <div className="flex-item">
            {first(data.selectedScorecards)?.id && (
              <CallScoreCarousel callScoreIds={data.scores} closeCarousel={closeCarousel} />
            )}
          </div>
        </div>
      ) : (
        <span className={`view-scores-page__overall-score ${successClass} mt`}>
          Aggregate Score is unavailable until a call is scored. Expand calls and use the Call
          Explorer to score a call.
        </span>
      )}
    </div>
  )
}

export const ViewScoresPage = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { data, loading, filters } = useSelector((state) => state.scorecards)
  const { targetScorecardConfig } = data
  // Copy this and pass it to this component
  const [showScoresTable, setShowScoresTable] = useState(false)
  const [printScores, setPrintScores] = useState(false)
  const { name: orgName } = useSelector((state) => state.organization)
  const { aggregateScores } = data
  const scoresLength = data.allScores?.length || 0

  // Determine if VOIP Customer ID column should be hidden for redacted orgs
  const shouldHideCustomerId = useShouldHideCustomerId(filters.organization)

  function getAggregateData() {
    const scorecardId = first(filters.scorecards)?.value
    if (scorecardId) {
      dispatch(fetchAggregateScoresByConfigId(scorecardId, null))
    } else {
      dispatch(fetchAggregateScores(null))
      dispatch(setData('displayNotableCalls', false))
    }
  }

  const toggleShowTable = (bool) => {
    setShowScoresTable(bool)
  }
  const compareTime = (timeStart, timeEnd) => {
    const startTime = moment(timeStart)
    const stopTime = moment(timeEnd)
    return Math.round(stopTime.diff(startTime, 'minutes', true)).toString()
  }

  const openCarousel = (scorecard) => {
    dispatch(selectScorecard(scorecard))
    dispatch(setData('scores', data.allScores))
    dispatch(setData('displayNotableCalls', false))
  }

  const handleFilterSubmit = async () => {
    setShowScoresTable(false)
    // Store the applied call type when filter is submitted
    dispatch(setData('appliedCallType', filters.callType))
    getAggregateData()
  }

  useEffect(() => {
    // Initialize appliedCallType on mount
    dispatch(setData('appliedCallType', filters.callType))

    // Dashboard agent links carry the filter in the URL so opening one in a
    // new tab (fresh store) still lands on that agent's scores.
    const { agent_id, agent_name } = queryString.parse(location.search)
    if (agent_id) {
      dispatch(
        setFilter('agents', [{ value: Number(agent_id), label: agent_name || `User ${agent_id}` }])
      )
    }
  }, [])

  const formatScoresData = (scorecardData, channelType) => {
    const channelName = CHANNEL_NAME_DISPLAY[channelType]

    return scorecardData.map((scorecard) => {
      const percentage = calculatePercentage(scorecard.aggregate_score, scorecard.possible_score)
      const displayAggregateScore = scorecard.aggregate_score !== null ? `${percentage}%` : 'N/A'

      return {
        ...scorecard,
        aggregate_score: {
          as: (
            <div
              data-testid="view-all-calls-percentage"
              className="clickable"
              onClick={() => openCarousel(scorecard)}
            >
              {displayAggregateScore}
            </div>
          ),
          value: (scorecard.aggregate_score / scorecard.possible_score) * 100,
        },
        start_time: {
          as: (
            <div
              data-testid="view-all-calls-start-time"
              className="clickable"
              onClick={() => openCarousel(scorecard)}
            >
              {formatTime(scorecard.start_time)}
            </div>
          ),
          value: scorecard.start_time,
        },
        name: {
          as: (
            <div
              data-testid="view-all-calls-user_name"
              className="clickable"
              onClick={() => openCarousel(scorecard)}
            >
              {scorecard.user_first_name} {scorecard.user_last_name}
            </div>
          ),
          value: `${scorecard.user_first_name} ${scorecard.user_last_name}`,
        },
        duration: {
          as: (
            <div
              data-testid="view-all-calls-call-duration"
              className="clickable"
              onClick={() => openCarousel(scorecard)}
            >
              {compareTime(scorecard.start_time, scorecard.end_time)} Minutes
            </div>
          ),
          value: parseInt(compareTime(scorecard.start_time, scorecard.end_time), 10),
        },
        voip_call_id: {
          as: (
            <div
              data-testid="view-all-calls-table-voip-id"
              className="clickable"
              onClick={() => openCarousel(scorecard)}
            >
              {scorecard.voip_call_id}
            </div>
          ),
          value: scorecard.voip_call_id,
        },
        voip_customer_id: {
          as: (
            <div
              data-testid="view-all-calls-table-voip-customer-id"
              className="clickable"
              onClick={() => openCarousel(scorecard)}
            >
              {scorecard.voip_customer_id}
            </div>
          ),
          value: scorecard.voip_customer_id,
        },
        exploreCall: {
          as: (
            <ExternalLink
              url={`../calls/${scorecard.call_id}`}
              label={`Explore ${channelName}`}
              id={scorecard.call_id}
            />
          ),
          value: scorecard.call_id,
        },
      }
    })
  }

  const handlePrint = async () => {
    await dispatch(fetchScoresByScorecardId())
    toggleShowTable(true)
    setPrintScores(true)
  }
  useEffect(() => {
    printScores ? window.print() : setPrintScores(false)
  }, [printScores])
  // Use appliedCallType from data, which only updates after clicking Filter button
  const channelType = data.appliedCallType
  const formattedRows = formatScoresData(data?.filteredScores, channelType)
  window.onafterprint = () => setPrintScores(false)
  const columns = getColumns(channelType, shouldHideCustomerId)

  return (
    <>
      <PageHeader
        title="Scores"
        actions={
          !isEmpty(data.aggregateScores) && (
            <Button
              onClick={handlePrint}
              icon={<IconFileTypePdf />}
              data-testid="export-pdf-button"
            >
              Export PDF
            </Button>
          )
        }
      />
      <ViewScoresFilters
        handleFilterSubmit={handleFilterSubmit}
        hide={{ sections: true, measures: true, criteria: true }}
      />
      {printScores && (
        <PrintScoresComponent
          orgName={orgName}
          filters={filters}
          numScores={scoresLength}
          aggregateScores={aggregateScores}
          targetScorecardConfig={targetScorecardConfig}
        />
      )}

      <div data-testid="view-scores-page">
        {loading.scores ? (
          <ViewScoresLoader />
        ) : (
          <ViewScoresPageContent
            printScores={printScores}
            showTable={showScoresTable}
            toggleTable={toggleShowTable}
          />
        )}
      </div>
      {showScoresTable && (
        <Card className="no-padding mt">
          <AdvancedTable
            loading={loading.scoresTable || loading.scorecards}
            rows={formattedRows}
            columns={columns}
            pagination={!printScores}
          />
        </Card>
      )}
    </>
  )
}

export default withRouter(ViewScoresPage)
